import { useState } from 'react';
import api, { assetUrl } from '@/lib/api';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Wand2, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import SEO from '@/components/SEO';
import { useLanguage } from '@/context/LanguageContext';

const STYLES = ['Minimal', 'Y2K', 'Sticker Pack', 'Watercolor', 'Neon', 'Vintage', 'Kawaii'];

const AIGenerator = () => {
  const [prompt, setPrompt] = useState('');
  const [style, setStyle] = useState('');
  const [category, setCategory] = useState('sticker');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const navigate = useNavigate();
  const { t } = useLanguage();

  const CATEGORIES = [
    { key: 'sticker', label: t('ai.catSticker', 'Sticker') },
    { key: 'phone_case', label: t('ai.catPhoneCase', 'Phone Case') },
    { key: 'label', label: t('ai.catLabel', 'Label') },
  ];

  const generate = async () => {
    if (!prompt.trim()) { toast(t('ai.enterPrompt', 'Enter a prompt')); return; }
    setLoading(true);
    try {
      const { data } = await api.post('/ai/generate-design', { prompt, style, category });
      setResults([data, ...results]);
      toast.success(t('ai.generated', 'Design generated!'));
    } catch (e) {
      toast.error(e.response?.data?.detail || t('ai.failed', 'Generation failed'));
    } finally { setLoading(false); }
  };

  const openInEditor = (r) => {
    const targetProduct = category === 'phone_case' ? 'pc-iphone-custom' : category === 'sticker' ? 'st-vinyl-custom' : 'lb-custom';
    localStorage.setItem('jw_editor_prefill', JSON.stringify({ image_url: assetUrl(r.image_url), category }));
    navigate(`/design/${targetProduct}`);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <SEO title="AI Design Generator — Create Custom Print Art with AI" description="Describe what you want to print and our AI generates unique artwork ready to drop into the design editor." path="/ai-generator" />
      <div className="flex items-center gap-2">
        <Wand2 className="w-6 h-6 text-orange-300" />
        <h1 className="font-display text-3xl sm:text-4xl font-bold">{t('ai.title', 'AI Design Generator')}</h1>
      </div>
      <p className="text-white/60 mt-2 max-w-xl">{t('ai.subtitle', 'Describe what you want to print. Our AI generates unique artwork you can drop straight into the editor.')}</p>

      <div className="grid lg:grid-cols-[420px_1fr] gap-6 mt-8">
        <div className="jw-card p-5">
          <label className="text-sm text-white/80">{t('ai.prompt', 'Prompt')}</label>
          <Textarea data-testid="ai-prompt-input" value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder={t('ai.promptPlaceholder', 'e.g. A cute cartoon avocado with sunglasses, sticker style')} className="mt-1 bg-white/[0.04] border-white/10 text-white placeholder:text-white/40 min-h-28" />

          <div className="mt-4">
            <label className="text-sm text-white/80">{t('ai.category', 'Category')}</label>
            <div className="flex gap-2 mt-2 flex-wrap">
              {CATEGORIES.map(c => (
                <button
                  key={c.key}
                  onClick={() => setCategory(c.key)}
                  data-testid={`ai-category-${c.key}-button`}
                  data-selected={category === c.key ? 'true' : 'false'}
                  className="jw-chip"
                  style={{ padding: '6px 14px', fontSize: '13px' }}
                >{c.label}</button>
              ))}
            </div>
          </div>

          <div className="mt-4">
            <label className="text-sm text-white/80">{t('ai.style', 'Style (optional)')}</label>
            <div className="flex gap-2 mt-2 flex-wrap">
              {STYLES.map(s => (
                <button
                  key={s}
                  onClick={() => setStyle(style === s ? '' : s)}
                  data-testid={`ai-style-${s.toLowerCase().replace(' ','-')}-button`}
                  data-selected={style === s ? 'true' : 'false'}
                  className="jw-chip"
                >{s}</button>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <Button onClick={generate} disabled={loading} data-testid="ai-generate-button" className="jw-btn-glossy rounded-[14px] h-12 px-6 w-full sm:w-auto">
              {loading ? <><Sparkles className="w-4 h-4 mr-2 animate-pulse" /> {t('ai.generating', 'Generating...')}</> : <><Sparkles className="w-4 h-4 mr-2" /> {t('ai.generate', 'Generate')}</>}
            </Button>
          </div>
          <p className="text-white/40 text-xs mt-3">{t('ai.policyNote', 'Please respect IP and content policy. Generated images are unique to your prompt.')}</p>
        </div>

        <div>
          {results.length === 0 && !loading && (
            <div className="jw-card p-16 text-center">
              <ImageIcon className="w-8 h-8 text-white/40 mx-auto" />
              <p className="text-white/60 mt-3">{t('ai.emptyResults', 'Your generated designs will appear here.')}</p>
            </div>
          )}
          {loading && (
            <div className="jw-card p-16 text-center">
              <Sparkles className="w-8 h-8 text-orange-300 mx-auto animate-pulse" />
              <p className="text-white/70 mt-3">{t('ai.creating', 'Creating your masterpiece...')}</p>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {results.map(r => (
              <Card key={r.id} className="jw-card overflow-hidden">
                <div className="aspect-square bg-[color:var(--jw-navy-950)] overflow-hidden">
                  <img src={assetUrl(r.image_url)} alt={r.prompt} className="w-full h-full object-cover" />
                </div>
                <div className="p-4">
                  <p className="text-white/80 text-sm line-clamp-2">{r.prompt}</p>
                  <div className="flex items-center gap-2 mt-3">
                    <Badge className="bg-white/[0.06] border-white/10 text-white/80">{t(`designCategory.${r.category}`, r.category.replace('_', ' '))}</Badge>
                    <div className="ml-auto">
                      <Button size="sm" onClick={() => openInEditor(r)} data-testid={`ai-use-${r.id}-button`} className="jw-btn-glossy rounded-md h-9 px-4"><Sparkles className="w-3.5 h-3.5 mr-1.5" /> {t('designs.useInEditor', 'Use in editor')}</Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIGenerator;
